import React, { useRef } from 'react';
import { pick, prop } from 'ramda';

import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';
import { Button } from '@interlayer/js/components/common/Button';

import { useTranslations } from '@core/js/hooks/i18n';
import { useAppSelector, useDeviceSelector, useUserSelector } from '@core/js/store/hooks';
import { clsb } from '@core/js/utils/classNames.util';

import { useDirectionalHover } from '@interlayer/js/app/components/main/Games/components/GameTile/components/DetailedTile/hooks/useDirectionalHover';
import { useOnFavouriteClick } from '@interlayer/js/app/components/main/Games/components/GameTile/components/DetailedTile/hooks/useOnFavouriteClick';
import { useGameComponentConfig } from '@interlayer/js/app/components/main/Games/hooks/useGameComponentConfig';
import { useGameInfo } from '@interlayer/js/app/components/main/Games/hooks/useGameInfo';
import { useGamesSelector } from '@interlayer/js/app/components/main/Games/store/context';
import { setGameFeedInStorage, setGameListInStorage } from '@interlayer/js/app/components/popups/GamesInfoPopup/utils';

import style from '../../DetailedTile.scss';

export const HoverBlock = ({
    gameData,
    gameCodes,
    classNames,
    containerRef,
    isShowInfo,
    feedName,
    isShowFavourites: isShowFavouritesProp
}) => {
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const isTouchDevice = useDeviceSelector(prop('isTouchDevice'));
    const infoPanelRef = useRef(null);
    const { feed: { name: gameFeedName = '' } = {} } = useGamesSelector();
    const { isFavoritesFetching, favoriteGames = [] } = useAppSelector(pick(['isFavoritesFetching', 'favoriteGames']));
    const translate = useTranslations('playtech.mobile.system.buzzGame.tile.');

    const buildCls = clsb([style, classNames]);

    const {
        showInfo = isShowInfo,
        showFavourites = isShowFavouritesProp
    } = useGameComponentConfig();

    const containerClass = buildCls(['info', isTouchDevice && 'touch']);

    useDirectionalHover({
        containerRef,
        panelRef: infoPanelRef,
        className: containerClass
    });

    const isShowFavourites = showFavourites && isLoggedIn && !isFavoritesFetching;
    const isFavorite = isShowFavourites && favoriteGames.includes(gameData.gameCode);

    const handleFavouriteClick = useOnFavouriteClick({ code: gameData.gameCode, isFavorite });

    const handlerOpenGameInfo = useGameInfo(gameData);

    const handleShowInfoClick = (event) => {
        setGameFeedInStorage(feedName || gameFeedName);
        setGameListInStorage(gameCodes);
        event.stopPropagation();

        handlerOpenGameInfo();
    };

    return (
        <div ref={infoPanelRef} className={containerClass}>
            {showInfo && (
                <div
                    onClick={handleShowInfoClick}
                    className={buildCls(['iconWrap'])}
                >
                    <SvgIcon
                        type="info"
                        className={buildCls(['infoIcon'])}
                    />
                </div>
            )}

            {isShowFavourites && (
                <SvgIcon
                    type={isFavorite ? 'favourite' : 'favourite-filled'}
                    className={buildCls(['infoFavourite'])}
                    onClick={handleFavouriteClick}
                />
            )}

            {!isTouchDevice && (
                <>
                    <div className={buildCls(['gameName'])}>{gameData.name}</div>

                    <Button
                        size="normal"
                        className={buildCls(['playButton'])}
                    >
                        {translate('cta')}
                    </Button>
                </>
            )}
        </div>
    );
};
